import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../utils/api";
import DataTable from "../components/DataTable";
import { useAuth } from "../contexts/AuthContext";

const ClientHistory = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [client, setClient] = useState(null);
  const { formatDate } = useAuth();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        // Fetch client info
        const clientRes = await api.get(`/admin/clients/${userId}`);
        setClient(clientRes.data.data);

        // Fetch user profile history
        const userHistory = clientRes.data.data.history.map((entry) => ({
          type: "profile",
          ...entry,
        }));

        // Fetch investments and their histories
        const investmentsRes = await api.get(
          `/admin/clients/${userId}/investments`
        );
        const investmentsHistory = investmentsRes.data.data.flatMap(
          (investment) =>
            investment.history.map((entry) => ({
              type: "investment",
              investmentId: investment.fdNumber,
              ...entry,
            }))
        );

        // console.log("Investment History:", investmentsHistory);

        // Combine and sort histories by timestamp (newest first)
        const combinedHistory = [...userHistory, ...investmentsHistory].sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        // console.log("User History:", userHistory);
        setHistory(combinedHistory);
        // console.log("Combined History:", combinedHistory);
      } catch (err) {
        setError("Failed to load history. Please try again.");
        console.error("fetchHistory error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [userId]);

  // Format the changes for better display
  const formatChanges = (changes) => {
    if (!changes) return "";

    // Handle nested changes
    const formattedChanges = [];

    // Process top-level changes
    Object.entries(changes).forEach(([key, value]) => {
      // Check if it's a normal old/new change
      if (
        value &&
        typeof value === "object" &&
        "old" in value &&
        "new" in value
      ) {
        formattedChanges.push(`${key}: ${value.old} → ${value.new}`);
      }
      // Check if it's a nested object with changes (like bankDetails)
      else if (value && typeof value === "object" && !Array.isArray(value)) {
        // Process nested objects
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          if (
            nestedValue &&
            typeof nestedValue === "object" &&
            "old" in nestedValue &&
            "new" in nestedValue
          ) {
            formattedChanges.push(
              `${key}.${nestedKey}: ${nestedValue.old} → ${nestedValue.new}`
            );
          }
        });
      }
    });

    // Join with line breaks instead of commas for better readability
    return formattedChanges.join("\n");
  };

  const columns = [
    {
      key: "entity",
      header: "Entity",
      render: (row) =>
        row.type === "profile" ? "Profile" : `Investment ${row.investmentId}`,
    },
    {
      key: "changes",
      header: "Changes",
      render: (row) => {
        const changes = formatChanges(row.changes);
        // Use CSS white-space property to preserve line breaks
        return <div style={{ whiteSpace: "pre-line" }}>{changes}</div>;
      },
    },
    { key: "updatedBy", header: "Updated By" },
    {
      key: "updatedAt",
      header: "Updated At",
      render: (row) => formatDate(row.updatedAt),
    },
  ];

  if (loading) {
    return (
      <div className="card main-content">
        <div className="text-center py-4">Loading client history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card main-content">
        <div className="text-center py-4 text-red-500">{error}</div>
        <div className="flex justify-center mt-4">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="btn btn-primary btn-sm"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card main-content">
      <h2>Client History: {client?.name || userId}</h2>
      <div className="mb-3">
        <Link to="/admin/dashboard" className="btn btn-primary btn-sm">
          Back to Dashboard
        </Link>
      </div>
      <DataTable
        columns={columns}
        data={history}
        emptyMessage="No history available for this client."
      />
    </div>
  );
};

export default ClientHistory;
